import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { omit } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";

import Stepper from "common/Stepper";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import BreadCrumb from "Components/Common/BreadCrumb";
import { applicationTypes } from "common/constant";
import DemandExtensionForm from "./DemandExtensionForm";
import Backdrop from "Components/Common/Backdrop";
import {
  temporaryApplicationOtherUtilityOptions,
  getOptionValue,
} from "common/enums";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useLazyGetOtherConsumerByIdQuery } from "apis/User";
import {
  useLazyGetOtherApplicationDetailQuery,
  usePostOtherUtilityApplicationDetailsMutation,
} from "apis/otherApplicationDetails";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import { myOtherRequestRoute } from "Routes/constants";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import {
  convertDocumentKeys,
  filterEmptyStrings,
  generateFormData,
} from "utils";
import { getDemandExtensionSchema } from "utils/otherUtilitiesValidationSchema";

const OtherUtilityDemandExtension = ({
  editApplicationQuery,
  handleClose = () => {},
  t,
}) => {
  const { data: { data: { items: applicantTypes = [] } = {} } = {} } =
    useApplicantTypeQuery();

  const { data: { data: premiseTypes } = {} } = usePremiseTypeQuery();

  const [submitApplication, { isLoading }] =
    usePostOtherUtilityApplicationDetailsMutation();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: customerDetails } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyGetOtherConsumerByIdQuery();

  const [
    getApplicationsData,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyGetOtherApplicationDetailQuery();

  const user = useSelector((state) => state.Login.user);
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [applicationData, setApplicationData] = useState({});

  const initialValues = useMemo(
    () => ({
      id: editApplicationQuery?.applicationId ?? 0,
      utilities:
        temporaryApplicationOtherUtilityOptions.filter(
          ({ value }) =>
            value ===
            (applicationData?.utilities?.[0] ?? customerDetails?.utility?.id)
        ) ?? [],
      applicationTypeId: applicationTypes["Demand Extension"],
      utilityConsumerId:
        customerDetails?.id ?? applicationData?.utilityConsumerId ?? "",
      consumerName: customerDetails?.consumerName || "",
      tariffName:
        applicationData?.tariffName ?? customerDetails?.tariffName ?? "",
      consumerNumber:
        applicationData?.consumerNumber ??
        customerDetails?.consumerNumber ??
        "",
      applicant: {
        id:
          applicationData?.applicant?.id ?? customerDetails?.applicant?.id ?? 0,
        applicantTypeId: customerDetails?.applicant?.applicantType?.id ?? "",
        applicantName: customerDetails?.applicant?.applicantName ?? "",
        panNumber:
          applicationData?.applicant?.panNumber ??
          customerDetails?.applicant?.panNumber ??
          "",
        gstNumber:
          applicationData?.applicant?.gstNumber ??
          customerDetails?.applicant?.gstNumber ??
          "",
        occupancyTypeId: customerDetails?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered:
          applicationData?.applicant?.isGSTRegistered ??
          customerDetails?.applicant?.isGSTRegistered ??
          "",
        addressType: customerDetails?.applicant?.addressType ?? "",
        correspondenceAddress:
          customerDetails?.applicant?.correspondenceAddress ?? "",
        city: customerDetails?.applicant?.city ?? "",
        pinCode: customerDetails?.applicant?.pinCode ?? "",
        email: customerDetails?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: customerDetails?.applicant?.mobileNumber ?? "",
        landLine: customerDetails?.applicant?.landLine ?? "",
        alternateEmail: customerDetails?.applicant?.alternateEmail
          ? customerDetails?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          customerDetails?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        // gender: applicationData?.authorizedPerson?.gender ?? "",
        // ageCategory: applicationData?.authorizedPerson?.ageCategory ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        premisesAreaId: customerDetails?.utilityAddress?.premiseArea?.id ?? "",
      },
      dcsDemand: {
        id: applicationData?.dcsDemand?.id ?? 0,
        contractDemand:
          applicationData?.dcsDemand?.contractDemand ??
          customerDetails?.dcsDemand?.totalContractDemand ??
          "",
        ultimateDemand: customerDetails?.dcsDemand?.ultimateDemand ?? "",
        waterFlowRate: customerDetails?.dcsDemand?.waterFlowRate ?? "",
        chilledWaterLineDiameter:
          customerDetails?.dcsDemand?.chilledWaterLineDiameter ?? "",
        increaseOfContractDemand:
          applicationData?.dcsDemand?.increaseOfContractDemand ?? 0,
      },
      waterSewageDemand: {
        waterDemandId: applicationData?.waterDemand?.id ?? 0,
        sewageDemandId: applicationData?.sewageDemand?.id ?? 0,
        waterDemand:
          applicationData?.waterDemand?.waterDemand ??
          applicationData?.sewageDemand?.waterDemand ??
          customerDetails?.waterDemand?.totalWaterDemand ??
          customerDetails?.sewageDemand?.totalWaterDemand ??
          0,
        flushing:
          applicationData?.waterDemand?.flushing ??
          applicationData?.sewageDemand?.flushing ??
          customerDetails?.waterDemand?.totalFlushing ??
          customerDetails?.sewageDemand?.totalFlushing ??
          0,
        domestic:
          applicationData?.waterDemand?.domestic ??
          applicationData?.sewageDemand?.domestic ??
          customerDetails?.waterDemand?.totalDomestic ??
          customerDetails?.sewageDemand?.totalDomestic ??
          0,
        gardening:
          applicationData?.waterDemand?.gardening ??
          applicationData?.sewageDemand?.gardening ??
          customerDetails?.waterDemand?.totalGardening ??
          customerDetails?.sewageDemand?.totalGardening ??
          0,
        increaseOfWaterDemand:
          applicationData?.waterDemand?.increaseOfWaterDemand ??
          applicationData?.sewageDemand?.increaseOfWaterDemand ??
          0,
        increaseOfFlushing:
          applicationData?.waterDemand?.increaseOfFlushing ??
          applicationData?.sewageDemand?.increaseOfFlushing ??
          0,

        increaseOfDomestic:
          applicationData?.waterDemand?.increaseOfDomestic ??
          applicationData?.sewageDemand?.increaseOfDomestic ??
          0,

        increaseOfGardening:
          applicationData?.waterDemand?.increaseOfGardening ??
          applicationData?.sewageDemand?.increaseOfGardening ??
          0,
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.consumerDuesOutstanding
          : "",
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.premisesDuesOutstanding
          : "",
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      extraValues: {
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
        premiseTypeId: customerDetails?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          customerDetails?.utilityAddress?.premiseUsagePurpose ?? "",
        isPanNumber: !!customerDetails?.applicant?.panNumber,
        isGstNumber: !!customerDetails?.applicant?.gstNumber,
        requiredDocumentsCount: 0,
        proofOfOwnershipTypes: [],
        applicantType: applicantTypes ?? [],
        isNonDomesticPremise:
          premiseTypes
            ?.find(
              (premiseType) =>
                premiseType.id ===
                customerDetails?.utilityAddress?.premiseType?.id
            )
            ?.name.toLowerCase() === "non-domestic",
      },
      inProgressApplicationId: customerDetails?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        customerDetails?.isAllApplicationsProcessCompleted ?? false,
      proofOfOwnershipDescription: "",
      payableBillAmount: customerDetails?.payableBillAmount ?? 0,
    }),
    [applicationData, customerDetails, premiseTypes]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: !(
      (customerDetails?.inProgressApplicationId !== applicationData?.id &&
        !customerDetails?.isAllApplicationsProcessCompleted) ||
      customerDetails?.payableBillAmount > 0
    )
      ? getDemandExtensionSchema[currentStep]
      : Yup.object({
          utilityConsumerId: Yup.number().required(
            "Customer number is required"
          ),
        }),
    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createDemandExtension(values);
      } else {
        const {
          waterSewageDemand: {
            increaseOfWaterDemand,
            increaseOfFlushing,
            increaseOfDomestic,
            increaseOfGardening,
          },
          utilities,
          inProgressApplicationId,
          payableBillAmount,
          isAllApplicationsProcessCompleted,
        } = values;

        if (
          !!inProgressApplicationId &&
          inProgressApplicationId !== applicationData?.id &&
          !isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (
          utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          ) &&
          !increaseOfWaterDemand &&
          !increaseOfDomestic &&
          !increaseOfFlushing &&
          !increaseOfGardening
        ) {
          toast.error("Any one value should be Increased");
        } else if (payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${payableBillAmount} RS. Please pay first to continue.`
          );
        } else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      }
    },
  });

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Demand Extension",
        isCompleted: currentStep >= 1,
        component: <DemandExtensionForm formik={formik} />,
      },
      {
        id: 2,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Document to Submit",
        isCompleted: currentStep >= 3,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 4,
        name: "Declaration",
        isCompleted: currentStep === 4,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  const { values, initialValues: formikInitialValues, handleSubmit } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const getCustomerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        editApplicationQuery &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  const getApplicationData = async (editApplicationQuery) => {
    try {
      const res = await getApplicationsData(editApplicationQuery);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  useEffect(() => {
    if (editApplicationQuery) {
      getApplicationData(editApplicationQuery);
    }
  }, [editApplicationQuery]);

  useEffect(() => {
    if (values.utilityConsumerId) {
      getCustomerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 2 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createDemandExtension = async (values) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";
    const id = values.applicant.id;

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(payloadWithUpdatedDocumentKeys, [
      "tariffName",
      "applicant.city",
      "consumerName",
      "inProgressApplicationId",
      "isAllApplicationsProcessCompleted",
      "payableBillAmount",
      "consumerNumber",
      "authorizedPerson.isAuthorizedPerson",
      "authorizedPerson.isUnRegisteredPerson",
      "extraValues",
      "utilityAddress",
      "declaration.isConsumerDuesOutstanding",
      "declaration.isPremisesDuesOutstanding",
      "authorizedPerson.isEditablePerson",
      "declaration.isFirmDuesOutstanding",
      "documents",
      "isIGSTExempted",
      "waterSewageDemand",
      imageKeyToRemove,
    ]);

    if (
      values.utilities.find(
        ({ value }) => value === getOptionValue("utilityOptions", "Sewage")
      )
    ) {
      applicationDetails.sewageDemand = {
        id: values.waterSewageDemand.sewageDemandId ?? 0,
        waterDemand: values.waterSewageDemand.waterDemand,
        flushing: values.waterSewageDemand.flushing,
        domestic: values.waterSewageDemand.domestic,
        gardening: values.waterSewageDemand.gardening,
        increaseOfWaterDemand: values.waterSewageDemand.increaseOfWaterDemand,
        increaseOfFlushing: values.waterSewageDemand.increaseOfFlushing,
        increaseOfDomestic: values.waterSewageDemand.increaseOfDomestic,
        increaseOfGardening: values.waterSewageDemand.increaseOfGardening,
      };
    }
    if (
      values.utilities.find(
        ({ value }) => value === getOptionValue("utilityOptions", "Water")
      )
    ) {
      applicationDetails.waterDemand = {
        id: values.waterSewageDemand.waterDemandId ?? 0,
        waterDemand: values.waterSewageDemand.waterDemand,
        flushing: values.waterSewageDemand.flushing,
        domestic: values.waterSewageDemand.domestic,
        gardening: values.waterSewageDemand.gardening,
        increaseOfWaterDemand: values.waterSewageDemand.increaseOfWaterDemand,
        increaseOfFlushing: values.waterSewageDemand.increaseOfFlushing,
        increaseOfDomestic: values.waterSewageDemand.increaseOfDomestic,
        increaseOfGardening: values.waterSewageDemand.increaseOfGardening,
      };
    }

    const currentGstNumber = editApplicationQuery
      ? applicationData?.applicant?.gstNumber
      : customerDetails?.applicant?.gstNumber;

    const currentPanNumber = editApplicationQuery
      ? applicationData?.applicant?.panNumber
      : customerDetails?.applicant?.panNumber;

    const filteredValues = filterEmptyStrings({
      ...applicationDetails,
      applicant: {
        ...applicationDetails.applicant,
        alternateEmail: applicationDetails.applicant.alternateEmail.length
          ? applicationDetails.applicant.alternateEmail.join(", ")
          : "",
        cityId,
        id:
          applicationDetails.applicant.gstNumber !== (currentGstNumber ?? "") ||
          applicationDetails.applicant.panNumber !== (currentPanNumber ?? "")
            ? 0
            : id,
      },
    });

    // remove entries that contain only id field
    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          (value && Object.keys(value).length > 1)
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();
    generateFormData(
      formData,
      omit(
        {
          ...payload,
          declaration: filteredValues.declaration,
          utilities: values.utilities.map(({ value }) => value),
        },
        values.utilities.find(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        )
          ? ""
          : "dcsDemand"
      )
    );

    try {
      await submitApplication(formData);
      handleClose();
      // If submitted then navigate to my-request
      if (!editApplicationQuery) {
        navigate(myOtherRequestRoute);
      }
    } catch (error) {}
  };

  return (
    <div className={!editApplicationQuery ? "h-100" : ""}>
      <Backdrop
        open={
          isApplicationDataLoading ||
          isConsumerDetailsLoading ||
          isApplicationDataFetching ||
          isConsumerDataFetching ||
          (!!editApplicationQuery && !customerDetails)
        }
      />
      {!editApplicationQuery && (
        <BreadCrumb
          title={t("Demand Extension")}
          pageTitle={t("Other Applications / Demand Alteration")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          validateDocument={validateDocument}
          isLoading={isLoading}
          formik={formik}
          isDraftDisabled
        />
      </div>
    </div>
  );
};
OtherUtilityDemandExtension.propTypes = {
  editApplicationQuery: PropTypes.object,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};
export default withTranslation()(OtherUtilityDemandExtension);
